// @ts-check
import { observeImage } from "../../platform/assets.js";

// Four fixed cels per direction. The second half reposes the SAME leg artwork:
// complete original boots and dark hip connectors, without replacement armor.
export const RUN_SIZE = 480;
export const RUN_STEP = 24;

/** @typedef {number[][]} Outline */
const RIGHT_CONTACT = {
  region: [40, 28, 376, 474], anchor: 250, baseline: 491,
  shoulders: [[142,249], [340,304]],
  arms: [
    [[32,210],[145,210],[145,231],[142,249],[149,267],[154,281],[153,343],[32,352]],
    [[355,252],[425,252],[425,366],[330,366],[329,344],[334,330],[340,313],[343,292]],
  ],
  hips: [[176, 355], [241, 370]],
  legs: [
    [[163,343],[168,355],[175,363],[188,367],[173,378],[163,387],[150,397],[141,409],[125,423],[111,429],[107,447],[99,464],[88,473],[76,467],[64,452],[59,434],[57,411],[58,393],[65,380],[81,377],[84,370],[98,365],[113,359],[127,353],[145,350],[153,350]],
    [[218,354],[246,362],[261,364],[272,376],[284,386],[289,383],[299,393],[315,417],[330,438],[341,440],[358,450],[372,462],[381,467],[385,481],[375,487],[335,494],[304,494],[278,493],[271,484],[269,475],[266,464],[259,449],[254,432],[253,406],[255,400],[247,397],[230,382],[222,371]],
  ],
  body: [[40,28],[416,28],[416,345],[340,345],[334,328],[321,347],[306,359],[286,365],[265,366],[238,362],[217,355],[211,371],[206,376],[192,377],[174,373],[167,367],[164,352],[156,338],[40,342]],
  angles: [25, 0],
};
const RIGHT_PASS = {
  region: [436, 26, 295, 475], anchor: 578, baseline: 494,
  shoulders: [[487,281], [669,294]],
  arms: [
    [[434,274],[488,274],[502,285],[508,314],[511,341],[515,381],[434,381]],
    [[664,235],[742,235],[742,338],[656,338],[657,317],[669,303],[674,288],[674,270],[670,252]],
  ],
  hips: [[544, 356], [594, 350]],
  legs: [
    [[532,363],[568,363],[568,375],[560,389],[560,412],[572,425],[572,442],[567,458],[579,464],[586,471],[595,482],[593,494],[576,498],[553,496],[531,492],[513,484],[509,476],[511,458],[512,436],[514,414],[521,395],[528,388],[529,370]],
    [[575,348],[596,350],[613,356],[619,356],[629,362],[633,377],[628,393],[617,414],[608,433],[607,440],[602,450],[589,453],[573,446],[567,431],[561,416],[558,403],[557,394],[562,380],[572,365],[583,359],[598,360],[600,352]],
  ],
  body: [[436,26],[731,26],[731,330],[640,330],[634,336],[615,334],[587,338],[574,348],[572,364],[529,364],[527,344],[511,341],[505,366],[477,374],[448,372],[436,363]],
  angles: [0, -25],
};
const LEFT_CONTACT = {
  region: [48, 516, 366, 468], anchor: 204, baseline: 976,
  shoulders: [[337,747], [132,781]],
  arms: [
    [[327,727],[425,727],[425,840],[336,840],[319,798],[315,770],[328,750]],
    [[40,725],[118,725],[129,746],[136,759],[139,793],[144,825],[40,825]],
  ],
  hips: [[199, 851], [266, 836]],
  legs: [
    [[175,848],[201,844],[229,840],[223,851],[212,870],[202,885],[205,901],[198,923],[182,950],[178,958],[183,964],[181,978],[156,981],[120,971],[95,961],[70,953],[65,943],[70,932],[77,923],[93,915],[108,916],[120,919],[125,904],[139,885],[158,871],[172,875],[182,879],[176,870],[173,863]],
    [[251,835],[273,821],[287,834],[299,841],[311,827],[323,828],[346,839],[362,852],[375,869],[380,880],[386,895],[384,919],[374,941],[358,951],[343,949],[331,938],[323,920],[320,906],[303,901],[281,888],[274,875],[263,869],[246,852]],
  ],
  body: [[48,516],[414,516],[414,838],[338,838],[321,806],[299,805],[284,820],[266,833],[246,841],[225,847],[186,851],[179,853],[175,865],[165,871],[155,869],[151,862],[145,837],[133,795],[124,801],[111,810],[83,804],[63,790],[48,769]],
  angles: [0, -25],
};
const LEFT_PASS = {
  region: [452, 512, 290, 474], anchor: 593, baseline: 976,
  shoulders: [[689,772], [524,763]],
  arms: [
    [[694,759],[751,759],[751,871],[672,871],[670,829],[680,797],[682,777]],
    [[445,702],[507,702],[524,725],[526,748],[529,786],[528,806],[445,806]],
  ],
  hips: [[574, 838], [620, 850]],
  legs: [
    [[557,826],[569,815],[583,816],[596,824],[600,838],[608,852],[617,870],[620,885],[616,899],[610,921],[595,934],[581,940],[565,936],[557,926],[554,908],[557,893],[552,882],[548,865],[548,847]],
    [[600,840],[634,838],[635,858],[633,874],[645,876],[650,891],[655,914],[655,940],[655,953],[660,959],[658,969],[638,977],[615,982],[588,982],[574,973],[575,960],[583,947],[589,942],[587,926],[587,908],[593,890],[603,879],[605,858]],
  ],
  body: [[452,512],[742,512],[742,856],[682,856],[672,809],[667,797],[650,810],[638,820],[624,829],[603,832],[597,823],[585,816],[572,814],[560,820],[554,831],[552,843],[534,844],[528,839],[525,797],[509,786],[493,793],[471,782],[452,760]],
  angles: [25, 0],
};
const SOURCES = [[LEFT_CONTACT, LEFT_PASS], [RIGHT_CONTACT, RIGHT_PASS]];
const ORIGINAL_LEGS = [
  [[390,807],[454,807],[466,824],[458,865],[469,910],[471,959],[460,978],[431,989],[382,987],[342,976],[334,952],[343,910],[342,860],[354,818],[375,804]],
  [[558,755],[637,755],[644,803],[655,831],[659,888],[652,929],[681,944],[697,968],[686,980],[655,986],[601,984],[545,977],[525,959],[530,925],[534,866],[548,813],[555,790]],
];

/** @param {CanvasRenderingContext2D} context @param {Outline} points */
function path(context, points) {
  context.moveTo(points[0][0], points[0][1]);
  for (const [x, y] of points.slice(1)) context.lineTo(x, y);
  context.closePath();
}

/** Remove the source's black backdrop before overlapping sprite parts.
 * @param {HTMLImageElement} source
 */
export function prepareRunSource(source) {
  const canvas = document.createElement("canvas");
  canvas.width = source.naturalWidth;
  canvas.height = source.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) return source;
  context.drawImage(source, 0, 0);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
  for (let index = 0; index < pixels.data.length; index += 4) {
    if (Math.max(pixels.data[index], pixels.data[index + 1], pixels.data[index + 2]) <= 10) pixels.data[index + 3] = 0;
  }
  context.putImageData(pixels, 0, 0);
  return canvas;
}

/**
 * Paint one complete, preselected pose. Q/P always come from the intact body
 * pose, which changes angle between contact and passing frames.
 * @param {CanvasRenderingContext2D} context
 * @param {HTMLImageElement | HTMLCanvasElement} source
 * @param {number} frame
 * @param {number} facing
 * @param {HTMLImageElement} original
 */
export function paintRunCel(context, source, frame, facing, original) {
  const pose = SOURCES[facing < 0 ? 0 : 1][frame % 2];
  const opposite = frame >= 2;
  context.save();
  const stepDown = frame === 2 ? (facing > 0 ? 13 : 10) : 0;
  context.translate(RUN_SIZE / 2 - pose.anchor, RUN_SIZE - 4 - pose.baseline + stepDown);
  // Near and far arms counter-swing with their legs. Shoulder caps stay on the
  // body; the far arm passes behind the P and the near arm passes in front.
  const armAngles = [
    [-45, -15, 0, -15][frame] * facing,
    [110, 65, 0, 65][frame] * facing,
  ];
  const drawArm = (/** @type {number} */ arm) => {
    const shoulder = pose.shoulders[arm];
    context.save();
    context.translate(shoulder[0], shoulder[1]);
    context.rotate(armAngles[arm] * Math.PI / 180);
    context.translate(-shoulder[0], -shoulder[1]);
    context.beginPath();
    path(context, pose.arms[arm]);
    context.clip();
    context.drawImage(source, 0, 0);
    context.restore();
  };
  const excludeSourceArms = () => {
    for (const arm of pose.arms) {
      context.beginPath();
      context.rect(0, 0, source.width, source.height);
      path(context, arm);
      context.clip("evenodd");
    }
  };
  drawArm(1);
  if (!opposite) {
    const [x, y, width, height] = pose.region;
    context.save();
    excludeSourceArms();
    context.drawImage(source, x, y, width, height, x, y, width, height);
    context.restore();
  } else {
    // Swap which hip each original leg starts from. Positive scale only:
    // even the boots keep their original facing and armor.
    const order = facing > 0 ? [0, 1] : [1, 0];
    const drawLeg = (/** @type {number} */ leg) => {
      const standing = frame === 3 && leg === order[0];
      if (standing) {
        // Use the complete, unoccluded standing leg from the canonical qp.png.
        // Uniform scaling preserves the boot's shape and armor proportions.
        const side = facing > 0 ? 1 : 0;
        const origin = side ? [597, 760] : [420, 808];
        const scale = side ? 0.6 : 0.77;
        const hip = pose.hips[1 - leg];
        context.save();
        context.translate(hip[0], pose.baseline - (989 - origin[1]) * scale);
        context.scale(scale, scale);
        context.translate(-origin[0], -origin[1]);
        context.beginPath();
        path(context, ORIGINAL_LEGS[side]);
        context.clip();
        context.drawImage(original, 0, 0);
        context.restore();
        return;
      }
      const artwork = pose;
      const part = leg;
      const origin = artwork.hips[part];
      const hip = [...pose.hips[1 - leg]];
      context.save();
      context.translate(hip[0], hip[1]);
      context.rotate(pose.angles[leg] * Math.PI / 180);
      context.translate(-origin[0], -origin[1]);
      context.beginPath();
      path(context, artwork.legs[part]);
      context.clip();
      context.beginPath();
      context.rect(0, 0, source.width, source.height);
      path(context, artwork.legs[1 - part]);
      context.clip("evenodd");
      context.beginPath();
      context.rect(0, 0, source.width, source.height);
      path(context, artwork.body);
      context.clip("evenodd");
      context.drawImage(source, 0, 0);
      context.restore();
    };
    drawLeg(order[0]);
    context.save();
    context.beginPath();
    path(context, pose.body);
    context.clip();
    excludeSourceArms();
    context.drawImage(source, 0, 0);
    context.restore();
    drawLeg(order[1]);
  }
  drawArm(0);
  context.restore();
}

/**
 * Pre-render the small sprite cycle once when its source loads.
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLImageElement} source
 * @param {HTMLImageElement} original
 * @param {() => void} onReady
 */
export function createRunRenderer(canvas, source, original, onReady) {
  const context = canvas.getContext("2d");
  /** @type {Map<string, HTMLCanvasElement>} */
  const cels = new Map();
  let prepared = false;
  function prepare() {
    // Dimensions can become available before decoding finishes. Preparing from
    // the other image's load event at that point would permanently cache blank cels.
    if (prepared || !context || !source.complete || !original.complete || !source.naturalWidth || !original.naturalWidth) return;
    prepared = true;
    const runSource = prepareRunSource(source);
    for (const facing of [-1, 1]) {
      for (let frame = 0; frame < 4; frame++) {
        const cel = document.createElement("canvas");
        cel.width = cel.height = RUN_SIZE;
        const celContext = cel.getContext("2d");
        if (!celContext) return;
        paintRunCel(celContext, runSource, frame, facing, original);
        cels.set(`${facing}:${frame}`, cel);
      }
    }
    onReady();
  }
  const release = [observeImage(source, prepare), observeImage(original, prepare)];
  return {
    dispose() { for (const cleanup of release) cleanup(); cels.clear(); },
    /** @param {number} frame @param {number} facing */
    render(frame, facing) {
      const cel = cels.get(`${facing}:${frame}`);
      if (!context || !cel) return false;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(cel, 0, 0, canvas.width, canvas.height);
      canvas.dataset.frame = String(frame);
      canvas.dataset.facing = facing < 0 ? "left" : "right";
      return true;
    },
  };
}
